import ModuleCard from 'components/cards/ModuleCard';
import CaregiverAvatar from 'components/messaging/CaregiverAvatar';
import { ReferentCard } from 'components/messaging/ReferentHero';
import { STORIES_SEARCH_PARAM } from 'components/messaging/MessagingHomeV3';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import dayjs from 'dayjs';
import { CaregiverIdentity, useCaregiverIdentity, useReferentCaregiver } from 'hooks/useCaregivers';
import { useConversationsByType } from 'hooks/useConversations';
import useMarkConversationAsRead from 'hooks/useMarkConversationAsRead';
import usePreferences from 'hooks/usePreferences';
import { cn } from 'lib/utils';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';
import { ConversationDTO } from 'services/api/models/ConversationDTO';
import { selectCurrentUser } from 'store/users/selectors';

/** Who answers a conversation: its caregiver, or the referent until one takes it. */
function useConversationCaregiver(conversation: ConversationDTO): CaregiverIdentity {
  const { referent } = useReferentCaregiver();
  return useCaregiverIdentity(conversation.current_owner) ?? referent;
}

function Sender({ avatars, title, subtitle }: { avatars: React.ReactNode; title: string; subtitle?: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex shrink-0 items-center">{avatars}</div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="text-base font-medium text-foreground">{title}</p>
        {subtitle && <p className="truncate text-sm text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
  );
}

/** « Ouvrir la messagerie », with the number of new messages across all conversations. */
function OpenMessagingButton({ unread }: { unread: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Button
      size="xl"
      className="w-full"
      data-testid="home-open-messaging-button"
      onClick={() => navigate(routes.messagingHome.route)}
    >
      {t('messagingV3.homeCard.openMessaging')}
      {unread > 0 && (
        <Badge data-testid="unread-messages-badge" variant="inverse">
          {unread}
        </Badge>
      )}
    </Button>
  );
}

/**
 * One conversation: her latest message when she wrote last (Figma 1a, in full
 * with the « professionnelle de santé » line when it is a proactive message the
 * patient has only received, 1b on two lines after), or that she will answer
 * when the patient wrote last (1e).
 */
function SingleConversation({
  conversation,
  unread,
  receivedProactiveMessage,
}: {
  conversation: ConversationDTO;
  unread: number;
  receivedProactiveMessage: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const markConversationAsRead = useMarkConversationAsRead();
  const currentUser = useSelector(selectCurrentUser);
  const { referent } = useReferentCaregiver();
  const caregiver = useConversationCaregiver(conversation);
  const lastMessage = conversation.last_message;
  const firstName = caregiver.firstName;
  const avatar = <CaregiverAvatar caregiver={caregiver} size="card" />;

  if (!lastMessage || lastMessage.sender?.id === currentUser?.id) {
    return (
      <>
        <Sender
          avatars={avatar}
          title={t('messagingV3.homeCard.exchangeWith', { firstName })}
          subtitle={t('messagingV3.homeCard.answersWithin', { count: 1 })}
        />
        <OpenMessagingButton unread={unread} />
      </>
    );
  }

  const preview = lastMessage.is_deleted ? t('chats.deletedLastMessage') : lastMessage.message_text;
  const role =
    caregiver.role &&
    (!conversation.current_owner || conversation.current_owner.id === referent.id
      ? t('messagingV3.referentRole', { role: caregiver.role })
      : caregiver.role);
  return (
    <>
      <Sender
        avatars={avatar}
        title={t('messagingV3.homeCard.wroteToYou', { firstName })}
        subtitle={[role, dayjs(lastMessage.sent_at).fromNow()].filter(Boolean).join(' · ')}
      />
      {receivedProactiveMessage ? (
        // She first goes through the messaging page, which introduces it
        <p className={previewClassName}>{preview}</p>
      ) : (
        // Then the bubble leads straight to the conversation
        <button
          type="button"
          data-testid="home-message-preview"
          aria-label={t('messagingV3.homeCard.openConversation', { firstName })}
          className={cn(previewClassName, 'line-clamp-2 cursor-pointer text-left')}
          onClick={() => {
            posthog.capture('home.message_preview_clicked');
            navigate(routes.conversation.route.replace(':conversationId', conversation.id));
            void markConversationAsRead(conversation.id);
          }}
        >
          {preview}
        </button>
      )}
      <div className="flex flex-col gap-5">
        <OpenMessagingButton unread={unread} />
        {receivedProactiveMessage && (
          <p className="text-center text-xs text-muted-foreground">{t('messagingV3.homeCard.realCaregiver')}</p>
        )}
      </div>
    </>
  );
}

const previewClassName = 'w-full rounded-lg rounded-tl-sm bg-chat-them p-4 text-base text-chat-them-foreground';

function StackedAvatar({ conversation }: { conversation: ConversationDTO }) {
  const caregiver = useConversationCaregiver(conversation);
  return <CaregiverAvatar caregiver={caregiver} size="card" className="not-first:-ml-7 ring-2 ring-card" />;
}

/**
 * Several conversations with different caregivers: their avatars stacked, and
 * whether they have answered (Figma 1c) or will answer (1d).
 */
function SeveralConversations({ conversations, unread }: { conversations: ConversationDTO[]; unread: number }) {
  const { t } = useTranslation();
  const latestUnread = conversations.find(c => c.number_of_unread_messages > 0);
  const avatars = conversations.slice(0, 3).map(c => <StackedAvatar key={c.id} conversation={c} />);

  return (
    <>
      {latestUnread ? (
        <Sender
          avatars={avatars}
          title={t('messagingV3.homeCard.theyAnswered')}
          subtitle={latestUnread.last_message && dayjs(latestUnread.last_message.sent_at).fromNow()}
        />
      ) : (
        <Sender
          avatars={avatars}
          title={t('messagingV3.homeCard.yourExchanges')}
          subtitle={t('messagingV3.homeCard.answersWithin', { count: conversations.length })}
        />
      )}
      <OpenMessagingButton unread={unread} />
    </>
  );
}

/** One conversation per caregiver (the latest), conversations not yet taken going to the referent. */
function byCaregiver(conversations: ConversationDTO[]) {
  const latest = new Map<string, ConversationDTO>();
  for (const conversation of conversations) {
    const caregiverId = conversation.current_owner?.id ?? 'referent';
    if (!latest.has(caregiverId)) latest.set(caregiverId, conversation);
  }
  return [...latest.values()];
}

/**
 * The homepage's messaging card in the redesign: the referring caregiver
 * introducing herself before any conversation, then where her conversations
 * stand (Figma « CAS - Messagerie »).
 */
export default function MessagingHomeCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const conversations = useConversationsByType('assigned');
  const { referent } = useReferentCaregiver();
  const { value: storiesSeen } = usePreferences(Keys.MESSAGING_STORIES_SEEN);

  const unread = conversations.reduce((total, c) => total + (c.number_of_unread_messages || 0), 0);
  // The conversations it is about: those with new messages, if any, one per caregiver
  const unreadConversations = conversations.filter(c => c.number_of_unread_messages > 0);
  const shown = byCaregiver(unreadConversations.length > 0 ? unreadConversations : conversations);
  const single = shown.length === 1 ? shown[0] : null;
  // A caregiver wrote to her first and she has not written yet: her only
  // conversation is a proactive message she has received
  const receivedProactiveMessage =
    conversations.length === 1 && !!conversations[0].last_message && conversations[0].patient_has_written === false;

  return (
    <ModuleCard>
      {conversations.length > 0 ? (
        <div className="flex flex-col gap-5" data-testid="home-latest-conversation">
          {single ? (
            <SingleConversation
              conversation={single}
              unread={unread}
              receivedProactiveMessage={receivedProactiveMessage}
            />
          ) : (
            <SeveralConversations conversations={shown} unread={unread} />
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <ReferentCard referent={referent} />
          {/* The dark call to action marks a first time (Figma) */}
          <Button
            variant="inverse"
            size="xl"
            className="w-full"
            onClick={() => {
              // The stories introduce the messaging once; then the card leads straight to it
              const showStories = storiesSeen !== true;
              posthog.capture('home.discover_messaging_clicked', { stories: showStories });
              navigate(
                showStories ? `${routes.messagingHome.route}?${STORIES_SEARCH_PARAM}=1` : routes.messagingHome.route,
              );
            }}
          >
            {t('messagingV3.homeCard.discover')}
          </Button>
        </div>
      )}
    </ModuleCard>
  );
}
